import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  AlertTriangle,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { LoadingPage } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { cn, formatRelativeTime } from '@/lib/utils';
import { deviceTypeLabel } from '@/lib/display-labels';
import { UserRole } from '@/types/api';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { useDevicesPage } from '../hooks/use-devices-page';
import type { DeviceSortBy, SortDir } from '../api/devices-api';
import { Select } from '@/components/ui/select';
import { DeviceModal } from './device-modal';
import { DeviceNameCell } from '@/components/ui/device-name-cell';
import { ConnectivityStatus } from '@/components/ui/connectivity-status';

export function DevicesPage() {
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const canDelete = activeRole === UserRole.Owner || activeRole === UserRole.Admin;
  const {
    devices,
    units,
    loading,
    refreshing,
    totalCount,
    totalPages,
    currentPage,
    searchInput,
    setSearchInput,
    debouncedSearch,
    selectedIds,
    bulkDeleting,
    toggleSelection,
    toggleSelectAllVisible,
    clearSelection,
    bulkDeleteSelected,
    handlePageChange,
    sortBy,
    sortDir,
    handleSortChange,
    isModalOpen,
    setIsModalOpen,
    editingDevice,
    deletingDevice,
    setDeletingDevice,
    isDeleting,
    handleDeleteConfirm,
    handleSaveDevice,
  } = useDevicesPage();

  // Header-checkbox indeterminate flag — true when *some* visible rows
  // are selected but not all. HTML doesn't expose `indeterminate` as
  // an attribute, so we set it via ref after each selection change.
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const visibleSelectedCount = devices.filter((d) => selectedIds.has(d.id)).length;
  const allVisibleSelected = devices.length > 0 && visibleSelectedCount === devices.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  if (loading) return <LoadingPage />;
  // True when the user is searching against a non-empty query and got
  // zero hits. Only fires after the debounced query has settled, so the
  // empty state doesn't flash mid-typing.
  const hasActiveSearch = debouncedSearch.trim().length > 0;
  const noMatches = hasActiveSearch && devices.length === 0;

  async function handleBulkDeleteConfirmed() {
    setConfirmBulkDelete(false);
    await bulkDeleteSelected();
  }

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Actions */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by serial or unit..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                aria-busy={refreshing || undefined}
              />
            </div>
            {canWrite && (
              <Button onClick={() => navigate('/setup')} className="flex-shrink-0">
                <Plus className="w-5 h-5" />
                <span className="hidden sm:inline">Add Device</span>
              </Button>
            )}
          </div>

          {/* Bulk-action bar — appears above the table when one or more
              rows are selected. Clear button closes the selection without
              firing an action. Delete is gated on canDelete so non-admins
              never see the destructive option. */}
          {selectedIds.size > 0 && (
            <div className="sticky top-0 z-20 flex items-center justify-between gap-3 rounded-xl border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 px-4 py-2.5 shadow-sm">
              <div className="flex items-center gap-3 min-w-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  aria-label="Clear selection"
                  className="flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
                <p className="text-sm font-medium text-primary-900 dark:text-primary-100 tabular-nums">
                  <span className="font-semibold">{selectedIds.size}</span>{' '}
                  {selectedIds.size === 1 ? 'device' : 'devices'} selected
                </p>
              </div>
              {canDelete && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmBulkDelete(true)}
                  isLoading={bulkDeleting}
                  disabled={bulkDeleting}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              )}
            </div>
          )}

          {/* Devices Table */}
          {devices.length === 0 ? (
            <Card variant="elevated">
              <EmptyState
                icon={<Search className="w-16 h-16" />}
                title={noMatches ? 'No devices match your search' : 'No devices found'}
                description={
                  noMatches
                    ? `Nothing matches "${debouncedSearch.trim()}". Try a different serial or unit number.`
                    : 'Add your first device to start monitoring.'
                }
                action={
                  noMatches
                    ? { label: 'Clear search', onClick: () => setSearchInput('') }
                    : canWrite
                      ? { label: 'Add Device', onClick: () => navigate('/setup') }
                      : undefined
                }
              />
            </Card>
          ) : (
            <Card variant="elevated" padding="none">
              <Table>
                <TableHeader>
                  <TableRow className="whitespace-nowrap">
                    {canDelete && (
                      <TableHead className="w-10">
                        <input
                          ref={headerCheckboxRef}
                          type="checkbox"
                          aria-label="Select all visible devices"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                        />
                      </TableHead>
                    )}
                    <TableHead>Device Name</TableHead>
                    <TableHead>Device Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Alarms</TableHead>
                    <TableHead>Last Reading</TableHead>
                    <TableHead>Last Seen</TableHead>
                    {canDelete && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => {
                    const isRowSelected = selectedIds.has(device.id);
                    return (
                    <TableRow
                      key={device.id}
                      className={cn(
                        'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50',
                        isRowSelected && 'bg-primary-50/60 dark:bg-primary-900/20',
                      )}
                      onClick={() => navigate(`/devices/${device.id}`)}
                    >
                      {canDelete && (
                        <TableCell
                          className="w-10"
                          // stopPropagation so a checkbox tap doesn't also
                          // fire the row-level navigate-to-detail handler.
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            aria-label={`Select device ${device.serialNumber}`}
                            checked={isRowSelected}
                            onChange={() => toggleSelection(device.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <DeviceNameCell device={device} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <Badge variant="info" size="sm">
                          {deviceTypeLabel[device.deviceType] ?? device.deviceType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {device.propertyName ? (
                          <div>
                            <button
                              className="text-primary-600 dark:text-primary-400 hover:underline"
                              onClick={(e) => { e.stopPropagation(); navigate(`/properties/${device.propertyId}`); }}
                            >
                              {device.propertyName}
                            </button>
                            {(device.buildingName || device.unitNumber) && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {device.buildingName}{device.buildingName && device.unitNumber ? ' \u2013 ' : ''}
                                {device.unitNumber ? `Unit ${device.unitNumber}` : ''}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ConnectivityStatus lastSeenAt={device.lastSeenAt} joinedAt={device.joinedAt} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {(device.activeAlerts?.length ?? 0) > 0 ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300 font-medium text-sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/alerts?deviceId=${device.id}`); }}
                            title="View alarms for this device"
                          >
                            <AlertTriangle className="w-4 h-4" />
                            {device.activeAlerts!.length}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                        {device.latestTransmission ? (
                          <span>
                            {(device.latestTransmission.computedWaterTotalization
                              ?? device.latestTransmission.waterTotalization
                            ).toLocaleString()} gal.
                          </span>
                        ) : (
                          <span className="text-gray-400">---</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                        {device.lastSeenAt
                          ? formatRelativeTime(device.lastSeenAt)
                          : 'Never'}
                      </TableCell>
                      {canDelete && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-error-600 hover:text-error-700"
                            onClick={(e) => { e.stopPropagation(); setDeletingDevice(device); }}
                            title="Delete device"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <span>Sort</span>
                  <div className="w-56">
                    <Select
                      value={`${sortBy}-${sortDir}`}
                      onChange={(val) => {
                        const [nextBy, nextDir] = val.split('-') as [DeviceSortBy, SortDir];
                        handleSortChange(nextBy, nextDir);
                      }}
                      options={[
                        { value: 'lastSeen-desc',     label: 'Last seen (newest first)' },
                        { value: 'lastSeen-asc',      label: 'Last seen (oldest first)' },
                        { value: 'propertyName-asc',  label: 'Property (A → Z)' },
                        { value: 'propertyName-desc', label: 'Property (Z → A)' },
                      ]}
                    />
                  </div>
                </label>
                {totalPages > 1 ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
                    {totalCount} total devices
                  </p>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {isModalOpen && (
        <DeviceModal
          device={editingDevice}
          units={units}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveDevice}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingDevice}
        title="Delete Device"
        description={`Are you sure you want to delete device "${deletingDevice?.devEui}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingDevice(null)}
        isLoading={isDeleting}
      />

      <ConfirmDialog
        isOpen={confirmBulkDelete}
        title={`Delete ${selectedIds.size} ${selectedIds.size === 1 ? 'device' : 'devices'}?`}
        description={
          <>
            <p>
              Permanently delete{' '}
              <strong>
                {selectedIds.size} {selectedIds.size === 1 ? 'device' : 'devices'}
              </strong>
              ? This cannot be undone.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              Each device will be removed from AWS IoT and the database.
              Bringing a meter back online requires a fresh re-pair via
              Setup Mode.
            </p>
          </>
        }
        confirmLabel={`Delete ${selectedIds.size}`}
        onConfirm={handleBulkDeleteConfirmed}
        onCancel={() => setConfirmBulkDelete(false)}
        isLoading={bulkDeleting}
      />
    </>
  );
}
